import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Topbar } from '../components/Topbar'
import { Badge } from '../components/Badge'
import {
  Loader2,
  Filter,
  TrendingUp,
  Map,
  Tags,
  Users,
  CreditCard,
  ShoppingCart,
  Package,
  CheckCircle2,
  Truck,
  ArrowDown,
  UserPlus,
  Repeat,
} from 'lucide-react'
import { apiGet, formatMXN } from '../lib/api'

// ─────────────────────────────────────────────────────────────────────────────
// Helpers de fecha — default último mes
// ─────────────────────────────────────────────────────────────────────────────

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10)
}

function defaultFrom(): string {
  const d = new Date()
  d.setUTCDate(d.getUTCDate() - 30)
  return isoDate(d)
}

function defaultTo(): string {
  return isoDate(new Date())
}

// ─────────────────────────────────────────────────────────────────────────────
// Date range picker compartido
// ─────────────────────────────────────────────────────────────────────────────

function DateRangePicker({
  from,
  to,
  onChange,
}: {
  from: string
  to: string
  onChange: (from: string, to: string) => void
}) {
  return (
    <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-ink-200 bg-white p-4">
      <div className="flex items-center gap-2 text-xs font-medium text-ink-500">
        <Filter className="size-3.5" /> Rango de fechas
      </div>
      <label className="flex flex-col text-xs text-ink-600">
        Desde
        <input
          type="date"
          value={from}
          max={to}
          onChange={(e) => onChange(e.target.value, to)}
          className="mt-1 rounded-md border border-ink-200 px-2 py-1 text-sm text-ink-900 focus:border-brand-500 focus:outline-none"
        />
      </label>
      <label className="flex flex-col text-xs text-ink-600">
        Hasta
        <input
          type="date"
          value={to}
          min={from}
          onChange={(e) => onChange(from, e.target.value)}
          className="mt-1 rounded-md border border-ink-200 px-2 py-1 text-sm text-ink-900 focus:border-brand-500 focus:outline-none"
        />
      </label>
      <div className="ml-auto flex gap-1">
        {[
          { label: '7d', days: 7 },
          { label: '30d', days: 30 },
          { label: '90d', days: 90 },
        ].map((p) => (
          <button
            key={p.label}
            type="button"
            onClick={() => {
              const t = new Date()
              const f = new Date()
              f.setUTCDate(f.getUTCDate() - p.days)
              onChange(isoDate(f), isoDate(t))
            }}
            className="rounded-md border border-ink-200 bg-white px-2.5 py-1 text-xs font-medium text-ink-700 hover:bg-ink-50"
          >
            {p.label}
          </button>
        ))}
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Tabs
// ─────────────────────────────────────────────────────────────────────────────

type TabId =
  | 'funnel'
  | 'ventas-geo'
  | 'top-categorias'
  | 'clientes-cohort'
  | 'por-pasarela'

interface TabDef {
  id: TabId
  label: string
  icon: typeof TrendingUp
}

const TABS: TabDef[] = [
  { id: 'funnel', label: 'Funnel', icon: TrendingUp },
  { id: 'ventas-geo', label: 'Ventas geo', icon: Map },
  { id: 'top-categorias', label: 'Top categorías', icon: Tags },
  { id: 'clientes-cohort', label: 'Clientes', icon: Users },
  { id: 'por-pasarela', label: 'Pasarelas', icon: CreditCard },
]

// ─────────────────────────────────────────────────────────────────────────────
// Tipos de respuesta
// ─────────────────────────────────────────────────────────────────────────────

interface FunnelResponse {
  from: string
  to: string
  stages: Array<{
    key: string
    name: string
    count: number
    conversionFromPrev: number
  }>
}

interface VentasGeoResponse {
  from: string
  to: string
  groupBy: 'state' | 'city'
  data: Array<{ region: string; count: number; total: number }>
  totals: { count: number; total: number }
}

interface TopCategoriasResponse {
  from: string
  to: string
  totals: { quantity: number; revenue: number }
  data: Array<{
    categoryId: string | null
    categoryName: string
    quantity: number
    revenue: number
  }>
}

interface ClientesCohortResponse {
  from: string
  to: string
  totalNuevos: number
  totalRecurrentes: number
  gmvNuevos: number
  gmvRecurrentes: number
  totalClientes: number
  totalGmv: number
}

interface PorPasarelaResponse {
  from: string
  to: string
  totals: { count: number; total: number; avgTicket: number }
  data: Array<{
    method: string
    count: number
    total: number
    avgTicket: number
  }>
}

// ─────────────────────────────────────────────────────────────────────────────
// Página
// ─────────────────────────────────────────────────────────────────────────────

export function AnalyticsAvanzado() {
  const [activeTab, setActiveTab] = useState<TabId>('funnel')
  const [from, setFrom] = useState(defaultFrom())
  const [to, setTo] = useState(defaultTo())

  return (
    <>
      <Topbar
        title="Analytics avanzado"
        subtitle="Funnel, geografía, categorías, cohort de clientes y pasarelas"
      />
      <div className="space-y-6 p-6">
        <div className="flex flex-wrap gap-1 rounded-xl border border-ink-200 bg-white p-1">
          {TABS.map((t) => {
            const Icon = t.icon
            const active = t.id === activeTab
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setActiveTab(t.id)}
                className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                  active
                    ? 'bg-brand-600 text-white'
                    : 'text-ink-700 hover:bg-ink-50'
                }`}
              >
                <Icon className="size-4" />
                {t.label}
              </button>
            )
          })}
        </div>

        <DateRangePicker
          from={from}
          to={to}
          onChange={(f, t) => {
            setFrom(f)
            setTo(t)
          }}
        />

        {activeTab === 'funnel' && <FunnelTab from={from} to={to} />}
        {activeTab === 'ventas-geo' && <VentasGeoTab from={from} to={to} />}
        {activeTab === 'top-categorias' && (
          <TopCategoriasTab from={from} to={to} />
        )}
        {activeTab === 'clientes-cohort' && (
          <ClientesCohortTab from={from} to={to} />
        )}
        {activeTab === 'por-pasarela' && (
          <PorPasarelaTab from={from} to={to} />
        )}
      </div>
    </>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Reusables
// ─────────────────────────────────────────────────────────────────────────────

function StateView({
  isLoading,
  error,
  empty,
  children,
}: {
  isLoading: boolean
  error: unknown
  empty?: boolean
  children: React.ReactNode
}) {
  if (isLoading) {
    return (
      <div className="grid place-items-center rounded-2xl border border-ink-200 bg-white py-20 text-ink-500">
        <Loader2 className="size-8 animate-spin" />
        <p className="mt-3 text-sm">Cargando reporte…</p>
      </div>
    )
  }
  if (error) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        {(error as Error).message}
      </div>
    )
  }
  if (empty) {
    return (
      <div className="rounded-2xl border border-ink-200 bg-white p-10 text-center text-sm text-ink-500">
        Sin datos en el rango seleccionado.
      </div>
    )
  }
  return <>{children}</>
}

// ─────────────────────────────────────────────────────────────────────────────
// 1. FUNNEL
// ─────────────────────────────────────────────────────────────────────────────

const FUNNEL_ICONS: Record<string, typeof ShoppingCart> = {
  carritos: ShoppingCart,
  creados: Package,
  pagados: CheckCircle2,
  entregados: Truck,
}

const FUNNEL_COLORS = [
  { bg: 'bg-amber-50', text: 'text-amber-700', fill: '#F59E0B' },
  { bg: 'bg-blue-50', text: 'text-blue-700', fill: '#3B82F6' },
  { bg: 'bg-emerald-50', text: 'text-emerald-700', fill: '#10B981' },
  { bg: 'bg-indigo-50', text: 'text-indigo-700', fill: '#6366F1' },
]

function FunnelTab({ from, to }: { from: string; to: string }) {
  const { data, isLoading, error } = useQuery<FunnelResponse>({
    queryKey: ['analytics', 'funnel', from, to],
    queryFn: () =>
      apiGet(`/api/admin/analytics/funnel?from=${from}&to=${to}`),
  })

  return (
    <StateView isLoading={isLoading} error={error}>
      {data && (
        <>
          {/* Stat cards con flechas entre etapas */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {data.stages.map((s, i) => {
              const Icon = FUNNEL_ICONS[s.key] ?? Package
              const col = FUNNEL_COLORS[i] ?? FUNNEL_COLORS[0]
              return (
                <div
                  key={s.key}
                  className="relative rounded-2xl border border-ink-200 bg-white p-5"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="text-xs font-medium text-ink-500">
                        {s.name}
                      </div>
                      <div className="mt-1 text-3xl font-extrabold text-ink-900">
                        {s.count.toLocaleString('es-MX')}
                      </div>
                      {i > 0 && (
                        <div className={`mt-1 text-xs font-medium ${col.text}`}>
                          {s.conversionFromPrev.toFixed(1)}% vs etapa anterior
                        </div>
                      )}
                    </div>
                    <div className={`grid size-10 place-items-center rounded-xl ${col.bg}`}>
                      <Icon className={`size-5 ${col.text}`} />
                    </div>
                  </div>
                  {i < data.stages.length - 1 && (
                    <div className="absolute -right-3 top-1/2 z-10 hidden -translate-y-1/2 lg:block">
                      <div className="grid size-6 place-items-center rounded-full border border-ink-200 bg-white text-ink-400">
                        <ArrowDown className="size-3 -rotate-90" />
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          {/* Funnel SVG */}
          <div className="rounded-2xl border border-ink-200 bg-white p-6">
            <h3 className="mb-4 text-sm font-semibold text-ink-900">
              Visualización del funnel
            </h3>
            <FunnelSvg stages={data.stages} />
          </div>
        </>
      )}
    </StateView>
  )
}

function FunnelSvg({ stages }: { stages: FunnelResponse['stages'] }) {
  const W = 600
  const H = 320
  const maxCount = Math.max(...stages.map((s) => s.count), 1)
  const rowH = H / stages.length
  const minW = 80

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" preserveAspectRatio="xMidYMid meet">
      {stages.map((s, i) => {
        const ratio = s.count / maxCount
        const w = Math.max(minW, ratio * W)
        const x = (W - w) / 2
        const y = i * rowH
        const col = FUNNEL_COLORS[i] ?? FUNNEL_COLORS[0]
        return (
          <g key={s.key}>
            <rect
              x={x}
              y={y + 4}
              width={w}
              height={rowH - 8}
              rx={6}
              fill={col.fill}
              opacity={0.85}
            />
            <text
              x={W / 2}
              y={y + rowH / 2 - 4}
              textAnchor="middle"
              fontSize={13}
              fontWeight={700}
              fill="#fff"
            >
              {s.name}
            </text>
            <text
              x={W / 2}
              y={y + rowH / 2 + 14}
              textAnchor="middle"
              fontSize={12}
              fill="#fff"
              opacity={0.95}
            >
              {s.count.toLocaleString('es-MX')}
              {i > 0 && ` · ${s.conversionFromPrev.toFixed(1)}%`}
            </text>
          </g>
        )
      })}
    </svg>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// 2. VENTAS GEO
// ─────────────────────────────────────────────────────────────────────────────

function VentasGeoTab({ from, to }: { from: string; to: string }) {
  const [groupBy, setGroupBy] = useState<'state' | 'city'>('state')

  const { data, isLoading, error } = useQuery<VentasGeoResponse>({
    queryKey: ['analytics', 'ventas-geo', from, to, groupBy],
    queryFn: () =>
      apiGet(
        `/api/admin/analytics/ventas-geo?from=${from}&to=${to}&groupBy=${groupBy}`,
      ),
  })

  const maxTotal = useMemo(() => {
    if (!data) return 0
    return Math.max(...data.data.map((r) => r.total), 1)
  }, [data])

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="grid grid-cols-2 gap-3 sm:flex sm:flex-wrap">
          <StatCard
            label="Total ventas"
            value={data ? formatMXN(data.totals.total) : '—'}
            color="emerald"
          />
          <StatCard
            label="# pedidos"
            value={data ? data.totals.count.toLocaleString('es-MX') : '—'}
            color="blue"
          />
        </div>
        <div className="inline-flex rounded-lg border border-ink-200 bg-white p-1 text-sm">
          {(['state', 'city'] as const).map((g) => (
            <button
              key={g}
              type="button"
              onClick={() => setGroupBy(g)}
              className={`rounded-md px-3 py-1 font-medium ${
                groupBy === g
                  ? 'bg-brand-600 text-white'
                  : 'text-ink-700 hover:bg-ink-50'
              }`}
            >
              {g === 'state' ? 'Estado' : 'Ciudad'}
            </button>
          ))}
        </div>
      </div>

      <StateView
        isLoading={isLoading}
        error={error}
        empty={data && data.data.length === 0}
      >
        {data && (
          <div className="overflow-hidden rounded-2xl border border-ink-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-ink-50">
                <tr className="text-left text-xs uppercase tracking-wider text-ink-500">
                  <th className="w-[30%] px-4 py-3 font-medium">
                    {groupBy === 'state' ? 'Estado' : 'Ciudad'}
                  </th>
                  <th className="px-4 py-3 font-medium">Distribución</th>
                  <th className="w-24 px-4 py-3 text-right font-medium">
                    Pedidos
                  </th>
                  <th className="w-32 px-4 py-3 text-right font-medium">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.data.map((r) => {
                  const pct = (r.total / maxTotal) * 100
                  return (
                    <tr
                      key={r.region}
                      className="border-t border-ink-100 hover:bg-ink-50"
                    >
                      <td className="px-4 py-3 font-medium text-ink-900">
                        {r.region}
                      </td>
                      <td className="px-4 py-3">
                        <div className="h-2 w-full overflow-hidden rounded-full bg-ink-100">
                          <div
                            className="h-full rounded-full bg-brand-500"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right text-ink-700">
                        {r.count.toLocaleString('es-MX')}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-ink-900">
                        {formatMXN(r.total)}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </StateView>
    </>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// 3. TOP CATEGORÍAS
// ─────────────────────────────────────────────────────────────────────────────

function TopCategoriasTab({ from, to }: { from: string; to: string }) {
  const { data, isLoading, error } = useQuery<TopCategoriasResponse>({
    queryKey: ['analytics', 'top-categorias', from, to],
    queryFn: () =>
      apiGet(`/api/admin/analytics/top-categorias?from=${from}&to=${to}`),
  })

  const maxRevenue = useMemo(() => {
    if (!data) return 0
    return Math.max(...data.data.map((r) => r.revenue), 1)
  }, [data])

  return (
    <>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <StatCard
          label="Categorías"
          value={data ? data.data.length.toLocaleString('es-MX') : '—'}
          color="violet"
        />
        <StatCard
          label="Unidades vendidas"
          value={data ? data.totals.quantity.toLocaleString('es-MX') : '—'}
          color="blue"
        />
        <StatCard
          label="Revenue total"
          value={data ? formatMXN(data.totals.revenue) : '—'}
          color="emerald"
        />
      </div>

      <StateView
        isLoading={isLoading}
        error={error}
        empty={data && data.data.length === 0}
      >
        {data && (
          <div className="overflow-hidden rounded-2xl border border-ink-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-ink-50">
                <tr className="text-left text-xs uppercase tracking-wider text-ink-500">
                  <th className="w-12 px-4 py-3 text-center font-medium">#</th>
                  <th className="w-[30%] px-4 py-3 font-medium">Categoría</th>
                  <th className="px-4 py-3 font-medium">Distribución</th>
                  <th className="w-24 px-4 py-3 text-right font-medium">Uds</th>
                  <th className="w-32 px-4 py-3 text-right font-medium">
                    Revenue
                  </th>
                  <th className="w-16 px-4 py-3 text-right font-medium">%</th>
                </tr>
              </thead>
              <tbody>
                {data.data.map((r, i) => {
                  const barPct = (r.revenue / maxRevenue) * 100
                  const sharePct =
                    data.totals.revenue > 0
                      ? (r.revenue / data.totals.revenue) * 100
                      : 0
                  return (
                    <tr
                      key={r.categoryId ?? r.categoryName}
                      className="border-t border-ink-100 hover:bg-ink-50"
                    >
                      <td className="px-4 py-3 text-center">
                        <span className="grid size-6 place-items-center rounded-md bg-violet-100 text-[10px] font-bold text-violet-700">
                          {i + 1}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-medium text-ink-900">
                        {r.categoryName}
                      </td>
                      <td className="px-4 py-3">
                        <div className="h-2 w-full overflow-hidden rounded-full bg-ink-100">
                          <div
                            className="h-full rounded-full bg-violet-500"
                            style={{ width: `${barPct}%` }}
                          />
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right text-ink-700">
                        {r.quantity.toLocaleString('es-MX')}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-ink-900">
                        {formatMXN(r.revenue)}
                      </td>
                      <td className="px-4 py-3 text-right text-xs font-medium text-ink-600">
                        {sharePct.toFixed(1)}%
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </StateView>
    </>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// 4. CLIENTES COHORT
// ─────────────────────────────────────────────────────────────────────────────

function ClientesCohortTab({ from, to }: { from: string; to: string }) {
  const { data, isLoading, error } = useQuery<ClientesCohortResponse>({
    queryKey: ['analytics', 'clientes-cohort', from, to],
    queryFn: () =>
      apiGet(`/api/admin/analytics/clientes-cohort?from=${from}&to=${to}`),
  })

  return (
    <StateView isLoading={isLoading} error={error}>
      {data && (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatCard
              label="Clientes totales"
              value={data.totalClientes.toLocaleString('es-MX')}
              color="blue"
            />
            <StatCard
              label="GMV total"
              value={formatMXN(data.totalGmv)}
              color="emerald"
            />
            <StatCard
              label="% Nuevos"
              value={
                data.totalClientes > 0
                  ? `${((data.totalNuevos / data.totalClientes) * 100).toFixed(1)}%`
                  : '—'
              }
              color="violet"
            />
            <StatCard
              label="% Recurrentes"
              value={
                data.totalClientes > 0
                  ? `${((data.totalRecurrentes / data.totalClientes) * 100).toFixed(1)}%`
                  : '—'
              }
              color="amber"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <CohortCard
              icon={UserPlus}
              label="Nuevos"
              clientes={data.totalNuevos}
              gmv={data.gmvNuevos}
              pct={
                data.totalClientes > 0
                  ? (data.totalNuevos / data.totalClientes) * 100
                  : 0
              }
              pctGmv={
                data.totalGmv > 0 ? (data.gmvNuevos / data.totalGmv) * 100 : 0
              }
              variant="success"
            />
            <CohortCard
              icon={Repeat}
              label="Recurrentes"
              clientes={data.totalRecurrentes}
              gmv={data.gmvRecurrentes}
              pct={
                data.totalClientes > 0
                  ? (data.totalRecurrentes / data.totalClientes) * 100
                  : 0
              }
              pctGmv={
                data.totalGmv > 0
                  ? (data.gmvRecurrentes / data.totalGmv) * 100
                  : 0
              }
              variant="info"
            />
          </div>
        </>
      )}
    </StateView>
  )
}

function CohortCard({
  icon: Icon,
  label,
  clientes,
  gmv,
  pct,
  pctGmv,
  variant,
}: {
  icon: typeof UserPlus
  label: string
  clientes: number
  gmv: number
  pct: number
  pctGmv: number
  variant: 'success' | 'info'
}) {
  const palette =
    variant === 'success'
      ? { bg: 'bg-emerald-50', text: 'text-emerald-700', bar: 'bg-emerald-500' }
      : { bg: 'bg-indigo-50', text: 'text-indigo-700', bar: 'bg-indigo-500' }

  return (
    <div className={`rounded-2xl border border-ink-200 ${palette.bg} p-6`}>
      <div className="flex items-start justify-between">
        <div>
          <div className={`text-xs font-semibold uppercase tracking-wider ${palette.text}`}>
            {label}
          </div>
          <div className="mt-2 text-4xl font-extrabold text-ink-900">
            {clientes.toLocaleString('es-MX')}
          </div>
          <div className="mt-1 text-xs text-ink-600">
            {pct.toFixed(1)}% de clientes
          </div>
        </div>
        <div className={`grid size-12 place-items-center rounded-2xl bg-white ${palette.text}`}>
          <Icon className="size-6" />
        </div>
      </div>

      <div className="mt-5 border-t border-white/60 pt-4">
        <div className="text-xs font-medium text-ink-600">GMV</div>
        <div className="mt-1 flex items-baseline justify-between">
          <div className="text-2xl font-bold text-ink-900">
            {formatMXN(gmv)}
          </div>
          <Badge variant={variant === 'success' ? 'success' : 'info'}>
            {pctGmv.toFixed(1)}%
          </Badge>
        </div>
        <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-white">
          <div
            className={`h-full rounded-full ${palette.bar}`}
            style={{ width: `${Math.min(100, pctGmv)}%` }}
          />
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// 5. POR PASARELA
// ─────────────────────────────────────────────────────────────────────────────

const PIE_COLORS = ['#E85230', '#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#6B7280']

function PorPasarelaTab({ from, to }: { from: string; to: string }) {
  const { data, isLoading, error } = useQuery<PorPasarelaResponse>({
    queryKey: ['analytics', 'por-pasarela', from, to],
    queryFn: () =>
      apiGet(`/api/admin/analytics/por-pasarela?from=${from}&to=${to}`),
  })

  return (
    <>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <StatCard
          label="Pedidos pagados"
          value={data ? data.totals.count.toLocaleString('es-MX') : '—'}
          color="blue"
        />
        <StatCard
          label="Total recaudado"
          value={data ? formatMXN(data.totals.total) : '—'}
          color="emerald"
        />
        <StatCard
          label="Ticket promedio"
          value={data ? formatMXN(data.totals.avgTicket) : '—'}
          color="violet"
        />
      </div>

      <StateView
        isLoading={isLoading}
        error={error}
        empty={data && data.data.length === 0}
      >
        {data && (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {/* Tabla */}
            <div className="lg:col-span-2 overflow-hidden rounded-2xl border border-ink-200 bg-white">
              <table className="w-full text-sm">
                <thead className="bg-ink-50">
                  <tr className="text-left text-xs uppercase tracking-wider text-ink-500">
                    <th className="px-4 py-3 font-medium">Pasarela</th>
                    <th className="w-24 px-4 py-3 text-right font-medium">
                      Pedidos
                    </th>
                    <th className="w-32 px-4 py-3 text-right font-medium">
                      Total
                    </th>
                    <th className="w-32 px-4 py-3 text-right font-medium">
                      Ticket promedio
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.data.map((r, i) => (
                    <tr
                      key={r.method}
                      className="border-t border-ink-100 hover:bg-ink-50"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span
                            className="inline-block size-3 rounded-full"
                            style={{
                              background: PIE_COLORS[i % PIE_COLORS.length],
                            }}
                          />
                          <span className="font-medium text-ink-900">
                            {r.method}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right text-ink-700">
                        {r.count.toLocaleString('es-MX')}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-ink-900">
                        {formatMXN(r.total)}
                      </td>
                      <td className="px-4 py-3 text-right text-ink-700">
                        {formatMXN(r.avgTicket)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pie SVG */}
            <div className="rounded-2xl border border-ink-200 bg-white p-5">
              <h3 className="mb-4 text-sm font-semibold text-ink-900">
                Distribución de monto
              </h3>
              <PieSvg
                slices={data.data.slice(0, 5).map((r, i) => ({
                  value: r.total,
                  color: PIE_COLORS[i % PIE_COLORS.length],
                  label: r.method,
                }))}
              />
            </div>
          </div>
        )}
      </StateView>
    </>
  )
}

function PieSvg({
  slices,
}: {
  slices: Array<{ value: number; color: string; label: string }>
}) {
  const size = 220
  const cx = size / 2
  const cy = size / 2
  const r = 90
  const total = slices.reduce((s, x) => s + x.value, 0)
  if (total <= 0) {
    return (
      <p className="text-sm text-ink-500">Sin datos para graficar.</p>
    )
  }

  let acc = 0
  const paths = slices.map((s) => {
    const startAngle = (acc / total) * Math.PI * 2 - Math.PI / 2
    acc += s.value
    const endAngle = (acc / total) * Math.PI * 2 - Math.PI / 2
    const large = endAngle - startAngle > Math.PI ? 1 : 0
    const x1 = cx + r * Math.cos(startAngle)
    const y1 = cy + r * Math.sin(startAngle)
    const x2 = cx + r * Math.cos(endAngle)
    const y2 = cy + r * Math.sin(endAngle)
    // Si una sola slice cubre todo, dibujamos un círculo entero para evitar
    // el bug del arco "vacío" cuando start==end.
    if (slices.length === 1 || s.value === total) {
      return (
        <circle key={s.label} cx={cx} cy={cy} r={r} fill={s.color} />
      )
    }
    const d = `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`
    return <path key={s.label} d={d} fill={s.color} />
  })

  return (
    <div className="flex flex-col items-center gap-4">
      <svg viewBox={`0 0 ${size} ${size}`} className="size-48">
        {paths}
        <circle cx={cx} cy={cy} r={40} fill="#fff" />
      </svg>
      <ul className="w-full space-y-1.5 text-xs">
        {slices.map((s) => {
          const pct = (s.value / total) * 100
          return (
            <li
              key={s.label}
              className="flex items-center justify-between gap-2"
            >
              <span className="flex items-center gap-2 truncate">
                <span
                  className="inline-block size-2.5 shrink-0 rounded-full"
                  style={{ background: s.color }}
                />
                <span className="truncate text-ink-700">{s.label}</span>
              </span>
              <span className="font-mono text-ink-900">
                {pct.toFixed(1)}%
              </span>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Stat card (compartido)
// ─────────────────────────────────────────────────────────────────────────────

type StatColor = 'emerald' | 'blue' | 'violet' | 'amber' | 'red'

function StatCard({
  label,
  value,
  color,
}: {
  label: string
  value: string | number
  color: StatColor
}) {
  const palette: Record<StatColor, string> = {
    emerald: 'text-emerald-700',
    blue: 'text-blue-700',
    violet: 'text-violet-700',
    amber: 'text-amber-700',
    red: 'text-red-700',
  }
  return (
    <div className="rounded-2xl border border-ink-200 bg-white p-4">
      <div className="text-xs font-medium text-ink-500">{label}</div>
      <div className={`mt-1 text-2xl font-extrabold ${palette[color]}`}>
        {value}
      </div>
    </div>
  )
}

export default AnalyticsAvanzado
